/**
 * VisualSeatMap — Sprint H buyer-facing SVG seat map.
 *
 * Renders event seats at their (x_percent, y_percent) coordinates inside a
 * 100×100 viewport. Supports pan / zoom / pinch on mobile via
 * `react-zoom-pan-pinch`. Falls back gracefully to a `null` render if seat
 * layout data is missing; the parent PublicEvent.jsx will show the list/grid
 * SeatPicker in that case.
 *
 * Contract:
 *   props.seats          — array from GET /api/public/events/{slug}/seats
 *                          (already ordered / merged with sold/held state).
 *                          Each seat: { id, seat_label, section, status,
 *                            x_percent, y_percent, rotation_deg, shape,
 *                            is_accessible }
 *   props.layout         — { preset, viewport_width, viewport_height,
 *                            background_svg_url } from
 *                          GET /api/public/events/{slug}/venue-layout
 *   props.selectedSeats  — array<string>  seat ids selected in parent
 *   props.onToggleSeat   — (seat) => void
 *   props.maxSelectable  — number      max seats the buyer may pick (== ticket qty)
 *
 * Accessibility:
 *   - Each seat is a real <button> with aria-label + aria-pressed.
 *   - Parent renders the list/grid fallback for screen-reader users via the
 *     "switch to list view" toggle button.
 */
import React, { useMemo } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Accessibility, ZoomIn, ZoomOut, Maximize2, List } from "lucide-react";
import { toast } from "sonner";

// A single seat glyph as a plain button (kept simple for a11y & perf)
function Seat({ seat, isSelected, disabled, onClick, size }) {
  const isSold = seat.status === "sold";
  const isUnavailable = seat.status === "unavailable" || seat.status === "held" || seat.status === "reserved";
  const isDisabled = seat.status === "disabled";
  const isAvailable = seat.status === "available";

  let fill = "#ffffff";
  let stroke = "var(--gt-line, #d4d4d8)";
  let text = "var(--gt-ink, #09090b)";
  let cursor = "pointer";

  if (isSelected) {
    fill = "var(--gt-accent, #ef4444)";
    stroke = "var(--gt-accent, #ef4444)";
    text = "#ffffff";
  } else if (isSold) {
    fill = "rgba(9,9,11,0.12)";
    stroke = "rgba(9,9,11,0.25)";
    text = "rgba(9,9,11,0.5)";
    cursor = "not-allowed";
  } else if (isUnavailable) {
    fill = "rgba(9,9,11,0.05)";
    stroke = "rgba(9,9,11,0.15)";
    text = "rgba(9,9,11,0.4)";
    cursor = "not-allowed";
  } else if (isDisabled) {
    fill = "rgba(9,9,11,0.02)";
    stroke = "rgba(9,9,11,0.1)";
    text = "rgba(9,9,11,0.3)";
    cursor = "not-allowed";
  }

  const clickable = isAvailable || isSelected;

  return (
    <g
      transform={`translate(${seat.x_percent} ${seat.y_percent}) rotate(${seat.rotation_deg || 0})`}
      data-testid={`vseat-${seat.seat_label}`}
      data-status={seat.status}
      data-selected={isSelected ? "true" : "false"}
    >
      <rect
        x={-size / 2}
        y={-size / 2}
        width={size}
        height={size * 0.9}
        rx={size * 0.18}
        fill={fill}
        stroke={stroke}
        strokeWidth={0.18}
        style={{ cursor: clickable && !disabled ? cursor : "not-allowed" }}
        onClick={(e) => {
          e.stopPropagation();
          if (!clickable || disabled) return;
          onClick(seat);
        }}
      />
      {isSold && (
        <line
          x1={-size / 2}
          y1={0}
          x2={size / 2}
          y2={0}
          stroke={text}
          strokeWidth={0.15}
          pointerEvents="none"
        />
      )}
      {seat.is_accessible && (
        <circle cx={size * 0.35} cy={-size * 0.35} r={size * 0.18} fill={isSelected ? "#ffffff" : "var(--gt-accent, #ef4444)"} />
      )}
      <text
        x={0}
        y={size * 0.15}
        fill={text}
        fontSize={size * 0.45}
        fontFamily="ui-monospace, SFMono-Regular, monospace"
        fontWeight="700"
        textAnchor="middle"
        pointerEvents="none"
      >
        {seat.seat_number ?? ""}
      </text>
    </g>
  );
}

// Section label overlay drawn at the average position of the section's seats
function SectionLabel({ section, cx, cy }) {
  return (
    <g transform={`translate(${cx} ${cy})`} pointerEvents="none">
      <rect
        x={-6}
        y={-1.4}
        width={12}
        height={2.8}
        rx={0.6}
        fill="rgba(9,9,11,0.85)"
      />
      <text
        x={0}
        y={0.65}
        fill="#ffffff"
        fontSize={1.4}
        fontFamily="system-ui, sans-serif"
        fontWeight="700"
        textAnchor="middle"
        letterSpacing={0.15}
      >
        {section.toUpperCase()}
      </text>
    </g>
  );
}

export default function VisualSeatMap({
  seats = [],
  layout = null,
  selectedSeats = [],
  onToggleSeat,
  maxSelectable = 0,
  onSwitchToList,
}) {
  // Derive viewport (default 100×100 for our presets)
  const vw = layout?.viewport_width || 100;
  const vh = layout?.viewport_height || 100;

  // Group seats by section for the label overlay + counts
  const { sectionCentres, sectionCounts } = useMemo(() => {
    const centres = {};
    const counts = {};
    for (const s of seats) {
      const sec = s.section || "Main";
      if (!centres[sec]) centres[sec] = { sx: 0, sy: 0, n: 0, avail: 0 };
      centres[sec].sx += s.x_percent;
      centres[sec].sy += s.y_percent;
      centres[sec].n += 1;
      if (s.status === "available") centres[sec].avail += 1;
    }
    const c = {};
    const cnt = {};
    for (const [sec, v] of Object.entries(centres)) {
      c[sec] = { cx: v.sx / v.n, cy: v.sy / v.n };
      cnt[sec] = { total: v.n, available: v.avail };
    }
    return { sectionCentres: c, sectionCounts: cnt };
  }, [seats]);

  const totalCounts = useMemo(() => {
    let a = 0, s = 0, u = 0;
    for (const seat of seats) {
      if (seat.status === "available") a++;
      else if (seat.status === "sold") s++;
      else u++;
    }
    return { available: a, sold: s, unavailable: u };
  }, [seats]);

  // Individual seat size in SVG units (0..100 space). Tuned so a 100-seat hall
  // renders comfortably touch-target-sized after pinch-zoom.
  const seatSize = 3.0;

  const selectedSet = useMemo(() => new Set(selectedSeats), [selectedSeats]);

  const handleToggle = (seat) => {
    if (selectedSet.has(seat.id)) {
      onToggleSeat(seat);
      return;
    }
    if (maxSelectable > 0 && selectedSeats.length >= maxSelectable) {
      toast.error(`You've picked ${maxSelectable} seat${maxSelectable === 1 ? "" : "s"}. Increase ticket qty to add more.`);
      return;
    }
    onToggleSeat(seat);
  };

  const hasLayoutData = seats.some((s) => typeof s.x_percent === "number" && typeof s.y_percent === "number");

  if (!hasLayoutData) {
    // Safety fallback — parent should have chosen list mode already, but if we
    // slipped through, tell caller to switch.
    return (
      <div className="p-6 text-sm text-[var(--gt-ink-3)] border border-[var(--gt-line)] rounded-sm" data-testid="visual-map-missing-layout">
        Seat map isn&apos;t available for this event. Please use the list picker.
        {onSwitchToList && (
          <button
            type="button"
            className="ml-2 underline"
            onClick={onSwitchToList}
            data-testid="switch-to-list-btn"
          >
            Switch to list view
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-10 border-t border-[var(--gt-line)] pt-8" data-testid="visual-seat-map">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div className="gt-label flex items-center gap-2">
          Choose your seats
          {maxSelectable > 0 && (
            <span className="ml-1 normal-case tracking-normal text-[var(--gt-ink-3)] text-xs font-normal">
              — pick {maxSelectable}
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--gt-ink-3)] font-mono" data-testid="vmap-inventory" />
      </div>

      {/* Legend + a11y toggle */}
      <div className="flex flex-wrap gap-3 items-center text-[10px] uppercase tracking-widest text-[var(--gt-ink-2)] mb-4">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border border-[var(--gt-line)] bg-white" /> Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[var(--gt-accent)]" /> Selected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[var(--gt-ink)]/20 border border-[var(--gt-ink)]/30" /> Taken
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Accessibility size={12} className="text-[var(--gt-accent)]" /> Accessible
        </span>
        {onSwitchToList && (
          <button
            type="button"
            onClick={onSwitchToList}
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-4 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
            data-testid="switch-to-list-btn"
            aria-label="Switch to list view (accessibility mode)"
          >
            <List size={11} /> List view
          </button>
        )}
      </div>

      {/* Pan / zoom container */}
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={5}
        wheel={{ step: 0.15 }}
        doubleClick={{ mode: "reset" }}
        limitToBounds
        pinch={{ step: 5 }}
        panning={{ velocityDisabled: true }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <div className="relative">
            <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => zoomIn()}
                aria-label="Zoom in"
                data-testid="vmap-zoom-in"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <ZoomIn size={14} />
              </button>
              <button
                type="button"
                onClick={() => zoomOut()}
                aria-label="Zoom out"
                data-testid="vmap-zoom-out"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <ZoomOut size={14} />
              </button>
              <button
                type="button"
                onClick={() => resetTransform()}
                aria-label="Reset view"
                data-testid="vmap-reset"
                className="w-8 h-8 rounded-sm bg-white border border-[var(--gt-line)] flex items-center justify-center shadow-sm hover:border-[var(--gt-ink)]"
              >
                <Maximize2 size={14} />
              </button>
            </div>

            <TransformComponent
              wrapperStyle={{ width: "100%" }}
              contentStyle={{ width: "100%" }}
            >
              <svg
                viewBox={`0 0 ${vw} ${vh}`}
                width="100%"
                style={{
                  minHeight: "420px",
                  maxHeight: "70vh",
                  background: "var(--gt-bg-surface, #fafafa)",
                  borderRadius: "4px",
                  touchAction: "none",
                }}
                role="img"
                aria-label={`Venue seat map`}
                data-testid="vmap-svg"
              >
                {/* Optional background image / stage marker */}
                {layout?.background_svg_url && (
                  <image
                    href={layout.background_svg_url}
                    x={0}
                    y={0}
                    width={vw}
                    height={vh}
                    opacity={0.35}
                  />
                )}

                {/* Stage / focal indicator at top for theatre-style presets */}
                {(layout?.preset === "theatre-curved" || layout?.preset === "hall-grid") && (
                  <g>
                    <rect x={vw * 0.15} y={vh * 0.02} width={vw * 0.7} height={vh * 0.04} rx={0.8} fill="rgba(9,9,11,0.85)" />
                    <text
                      x={vw / 2}
                      y={vh * 0.05}
                      fill="#fff"
                      fontSize={1.8}
                      fontFamily="system-ui, sans-serif"
                      fontWeight="700"
                      textAnchor="middle"
                      letterSpacing={0.5}
                    >
                      STAGE
                    </text>
                  </g>
                )}

                {/* Field marker for arena-bowl */}
                {layout?.preset === "arena-bowl" && (
                  <g>
                    <rect x={vw * 0.3} y={vh * 0.3} width={vw * 0.4} height={vh * 0.4} rx={1.5} fill="rgba(16,185,129,0.15)" stroke="rgba(16,185,129,0.4)" strokeWidth={0.2} />
                    <text
                      x={vw / 2}
                      y={vh / 2}
                      fill="rgba(9,9,11,0.4)"
                      fontSize={3}
                      fontFamily="system-ui, sans-serif"
                      fontWeight="700"
                      textAnchor="middle"
                    >
                      FIELD
                    </text>
                  </g>
                )}

                {/* Seats */}
                {seats.map((seat) => (
                  <Seat
                    key={seat.id}
                    seat={seat}
                    isSelected={selectedSet.has(seat.id)}
                    onClick={handleToggle}
                    size={seatSize}
                  />
                ))}

                {/* Section labels */}
                {Object.entries(sectionCentres).map(([sec, { cx, cy }]) => (
                  <SectionLabel key={sec} section={sec} cx={cx} cy={Math.max(3, cy - 5)} />
                ))}
              </svg>
            </TransformComponent>

            <p className="mt-3 text-[10px] text-center uppercase tracking-widest text-[var(--gt-ink-3)]">
              Pinch or scroll to zoom · drag to pan · tap a seat to select
            </p>
          </div>
        )}
      </TransformWrapper>

      {/* Per-section availability strip */}
      <div className="mt-4 flex flex-wrap gap-3 text-[10px] text-[var(--gt-ink-2)]" data-testid="vmap-section-counts">
        {Object.entries(sectionCounts).map(([sec, { available, total }]) => (
          <span key={sec} className="inline-flex items-center gap-1.5">
            <span className="uppercase tracking-widest font-semibold">{sec}</span>
            <span className="font-mono">{available}/{total}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Silence unused var warning until wrapperRef is used for programmatic zoom
export { Seat, SectionLabel };
